import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { validateEmail } from 'data/helpers'
import { fetchValidateEmployerUserEmail, validateEmployerUserEmailLoading } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import { isConsumerEmailDomain } from './consumerEmailDomains'

const EmployerUserEmailStep = ({ wizardData, setWizardData, onNext, onBack }) => {
  const dispatch = useDispatch()
  const { validatingEmail, emailValidation } = useSelector(state => state.employerUserSignup)

  const [email, setEmail] = useState(wizardData.email || '')
  const [touched, setTouched] = useState(false)
  const [attestation, setAttestation] = useState(false)
  const inputRef = useRef(null)
  const hasSubmittedRef = useRef(false)
  const hasTrackedFocusRef = useRef(false)
  const hasTrackedConsumerWarningRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (hasSubmittedRef.current && !validatingEmail && emailValidation) {
      if (emailValidation.isValid) {
        dispatch(
          fetchTrackEvent({
            eventName: 'signup_email_submitted',
            eventType: ProductAnalyticsEventTypes.ACTION,
            distinctId: email,
            eventData: { isConsumerEmail: false },
          }),
        )
        setWizardData(prev => ({ ...prev, email }))
        onNext()
      } else if (emailValidation.reason === 'ConsumerDomain' && attestation) {
        dispatch(
          fetchTrackEvent({
            eventName: 'signup_email_submitted',
            eventType: ProductAnalyticsEventTypes.ACTION,
            distinctId: email,
            eventData: { isConsumerEmail: true },
          }),
        )
        setWizardData(prev => ({ ...prev, email }))
        onNext()
      } else if (emailValidation.reason === 'ExistingUnverifiedUser') {
        dispatch(
          fetchTrackEvent({
            eventName: 'signup_claiming_existing_account',
            eventType: ProductAnalyticsEventTypes.ACTION,
            distinctId: email,
            eventData: { existingEmployerName: emailValidation.existingEmployerName },
          }),
        )
        setWizardData(prev => ({
          ...prev,
          email,
          isClaimingExistingAccount: true,
          employerId: emailValidation.existingEmployerId,
          employerName: emailValidation.existingEmployerName,
        }))
        onNext()
      } else if (emailValidation.reason === 'AlreadyRegistered') {
        dispatch(
          fetchTrackEvent({
            eventName: 'signup_email_rejected',
            eventType: ProductAnalyticsEventTypes.ACTION,
            distinctId: email,
            eventData: { reason: 'AlreadyRegistered' },
          }),
        )
      }
    }
  }, [validatingEmail, emailValidation, email, attestation, setWizardData, onNext, dispatch])

  const isConsumerDomain = touched && isConsumerEmailDomain(email)
  const alreadyRegisteredError = emailValidation && !emailValidation.isValid && emailValidation.reason === 'AlreadyRegistered'

  useEffect(() => {
    if (isConsumerDomain && !hasTrackedConsumerWarningRef.current) {
      hasTrackedConsumerWarningRef.current = true
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_consumer_email_warning_shown',
          eventType: ProductAnalyticsEventTypes.ACTION,
          eventData: { emailDomain: email.split('@')[1] },
        }),
      )
    }
  }, [isConsumerDomain, email, dispatch])

  const handleEmailFocus = () => {
    if (!hasTrackedFocusRef.current) {
      hasTrackedFocusRef.current = true
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_email_focused',
          eventType: ProductAnalyticsEventTypes.ACTION,
        }),
      )
    }
  }

  const handleAttestationChange = checked => {
    setAttestation(checked)
    if (checked) {
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_consumer_email_attested',
          eventType: ProductAnalyticsEventTypes.ACTION,
          eventData: { emailDomain: email.split('@')[1] },
        }),
      )
    }
  }

  const handleEmailChange = value => {
    setEmail(value)
    setAttestation(false)
    if (emailValidation) {
      dispatch(validateEmployerUserEmailLoading())
    }
  }

  const handleSubmit = () => {
    setTouched(true)

    if (!validateEmail(email)) {
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_email_validation_error',
          eventType: ProductAnalyticsEventTypes.ACTION,
          eventData: { reason: 'invalid_email' },
        }),
      )
      return
    }
    if (isConsumerEmailDomain(email) && !attestation) return

    hasSubmittedRef.current = true
    dispatch(fetchValidateEmployerUserEmail(email))
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSubmit()
  }

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Get started with MyStethi
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
        See how many candidates match your role and make your job searchable by active job-seekers.
      </Typography>

      {alreadyRegisteredError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          An account with this email already exists.{' '}
          <Box component='a' href='/login' sx={{ color: 'inherit', fontWeight: 600 }}>
            Log in instead.
          </Box>
        </Alert>
      )}

      <TextField
        inputRef={inputRef}
        label='Work Email'
        type='email'
        value={email}
        onChange={e => handleEmailChange(e.target.value)}
        onFocus={handleEmailFocus}
        onBlur={() => setTouched(true)}
        onKeyDown={handleKeyDown}
        fullWidth
        error={
          (touched && !validateEmail(email)) ||
          alreadyRegisteredError
        }
        helperText={
          touched && !validateEmail(email)
            ? 'Enter a valid email address.'
            : 'Enter the email address from the healthcare organization you work for.'
        }
        sx={{ mb: isConsumerDomain ? 2 : 3 }}
      />

      {isConsumerDomain && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            This looks like a personal email address. We recommend using your employer email for instant verification.
          </Typography>
          <Typography variant='body2'>
            If you are an in-house recruiter and this is your proper work email, you can still create an account. We will need to manually verify your organization, which may take up to 48 hours.
          </Typography>
        </Alert>
      )}

      {isConsumerDomain && (
        <FormControlLabel
          control={
            <Checkbox
              checked={attestation}
              onChange={e => handleAttestationChange(e.target.checked)}
            />
          }
          label={
            <Typography variant='body2' color='text.secondary'>
              I am directly employed by a healthcare organization. I am not a third-party recruiter or staffing agency representative.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mb: 3 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        {onBack && (
          <Button onClick={onBack} sx={{ flex: 0 }}>
            Back
          </Button>
        )}
        <Button
          variant='contained'
          fullWidth
          onClick={handleSubmit}
          disabled={validatingEmail || (isConsumerDomain && !attestation)}
          startIcon={validatingEmail ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {validatingEmail ? 'Checking...' : 'Continue'}
        </Button>
      </Box>
    </Box>
  )
}

export default EmployerUserEmailStep
